import { useMemo } from 'react';
import { ZipDeviceControlUnit } from '../../controlUnit/controlUnit.model';
import { useDevicesControlUnits } from '../../controlUnit/hooks/useDevicesControlUnits';
import useChannelsFromComposableDevice from '../../composableDevice/hooks/useChannelsFromComposableDevice';

export function useControlUnitsInComposableDevice(groupId: string) {
  const [originalDevicesControlUnits] = useDevicesControlUnits();
  const { data: composableChannels } = useChannelsFromComposableDevice(groupId);

  // Create enhanced device control units with composable device names
  const enhancedDevicesControlUnits = useMemo((): ZipDeviceControlUnit[] => {
    // Create a map for quick lookup of composable device names

    const composableNamesMap = new Map<string, string>();
    composableChannels.forEach((channel) => {
      if (channel.name) {
        const key = `${channel.deviceID}-${channel.cuID}-${channel.channelID}`;
        composableNamesMap.set(key, channel.name);
      }
    });

    return originalDevicesControlUnits
      .map((device) => ({
        ...device,
        controlUnits: device.controlUnits
          ?.map((cu) => ({
            ...cu,
            channels: cu.channels
              ?.map((channel) => {
                const key = `${device.deviceId}-${cu.id}-${channel.id}`;
                const composableName = composableNamesMap.get(key);
                // Only return channels that have composable names
                return composableName
                  ? {
                      ...channel,
                      name: composableName,
                      // Store original name for reference
                      originalName: channel.name,
                    }
                  : null;
              })
              .filter((channel) => channel !== null), // Remove null entries
            events: cu.events
              .map((event) => {
                const key = `${device.deviceId}-${cu.id}-${event.id}`;
                const composableName = composableNamesMap.get(key);
                // Only return events that have composable names
                return composableName
                  ? {
                      ...event,
                      name: composableName,
                      // Store original name for reference
                      originalName: event.name,
                    }
                  : null;
              })
              .filter((event) => event !== null), // Remove null entries
          }))
          .filter((cu) => {
            // Remove control units that have no channels or events with composable names
            const hasChannels = cu.channels && cu.channels.length > 0;
            const hasEvents = cu.events && cu.events.length > 0;
            return hasChannels || hasEvents;
          }),
      }))
      .filter((device) => {
        // Remove devices that have no control units with composable channels/events
        return device.controlUnits && device.controlUnits.length > 0;
      });
  }, [originalDevicesControlUnits, composableChannels]);

  return enhancedDevicesControlUnits;
}
